import React from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Spinning } from 'grommet-controls';
import {
  ValidatorDepositStatus,
  BeaconValidatorStatus,
} from '../../utils/validatorStatusChecker';
import { ETHERSCAN_URL, BEACONCHAIN_URL } from '../../utils/envVars';
import { Link } from '../../pulsechain_components/Link';

interface ColorShade {
  light?: string;
  medium?: string;
  dark?: string;
}

interface ThemeColors {
  green: ColorShade;
  yellow: ColorShade;
  blue: ColorShade;
  red: ColorShade;
  gray: ColorShade & { lightest?: string; darkish?: string };
}

type StatusVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

const getVariantColor = (
  theme: ThemeColors,
  variant: StatusVariant
): string => {
  switch (variant) {
    case 'success':
      return theme.green.dark || '#26AB83';
    case 'warning':
      return theme.yellow.dark || '#f0ad4d';
    case 'error':
      return theme.red.medium || '#912d2c';
    case 'info':
      return theme.blue.dark || '#0F2A43';
    case 'neutral':
    default:
      return theme.gray.medium || '#6b7280';
  }
};

const StatusContainer = styled.div<{ variant: StatusVariant }>`
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${p => getVariantColor(p.theme, p.variant)}20;
  border: 1px solid ${p => getVariantColor(p.theme, p.variant)};
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusTitle = styled.span`
  font-weight: bold;
  font-size: 14px;
`;

const StatusDetail = styled.div`
  font-size: 12px;
  color: ${p => p.theme.gray?.light || '#a0a0a0'};
  margin-top: 4px;
`;

const StatusBadge = styled.span<{ variant: StatusVariant }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${p => getVariantColor(p.theme, p.variant)};
  color: white;
`;

const ValidatorIndex = styled.span`
  font-weight: bold;
  color: ${p => p.theme.green?.light || '#26AB83'};
`;

const DepositIndex = styled.span`
  font-weight: bold;
  color: ${p => p.theme.blue?.light || '#3b82f6'};
`;

const ExternalLink = styled(Link)`
  font-size: 12px;
  margin-top: 4px;
  display: inline-block;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
`;

interface ValidatorStatusDisplayProps {
  status: ValidatorDepositStatus;
  beaconStatus?: BeaconValidatorStatus | string | null;
  beaconIndex?: number | null;
  beaconBalance?: string | null;
  execDeposited?: boolean;
  execTxHash?: string | null;
  execBlockNumber?: number | null;
  execDepositIndex?: number | null;
  pubkey?: string;
  isLoading?: boolean;
  compact?: boolean;
}

export const ValidatorStatusDisplay: React.FC<ValidatorStatusDisplayProps> = ({
  status,
  beaconStatus,
  beaconIndex,
  beaconBalance,
  execDeposited,
  execTxHash,
  execBlockNumber,
  execDepositIndex,
  pubkey,
  isLoading = false,
  compact = false,
}) => {
  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinning kind="pulse" />
        <span>
          <FormattedMessage defaultMessage="Checking status..." />
        </span>
      </LoadingContainer>
    );
  }

  const getStatusVariant = (): StatusVariant => {
    switch (status) {
      case ValidatorDepositStatus.ACTIVE:
        return 'success';
      case ValidatorDepositStatus.PENDING_INIT:
      case ValidatorDepositStatus.PENDING_QUEUED:
      case ValidatorDepositStatus.DEPOSITED_WAITING:
        return 'warning';
      case ValidatorDepositStatus.NOT_DEPOSITED:
        return 'neutral';
      case ValidatorDepositStatus.EXITING:
      case ValidatorDepositStatus.EXITED:
      case ValidatorDepositStatus.WITHDRAWN:
        return 'info';
      case ValidatorDepositStatus.SLASHED:
      case ValidatorDepositStatus.ERROR:
        return 'error';
      default:
        return 'neutral';
    }
  };

  const renderStatusContent = () => {
    switch (status) {
      case ValidatorDepositStatus.NOT_DEPOSITED:
        return (
          <StatusContainer variant="neutral">
            <StatusRow>
              <StatusBadge variant="neutral">
                <FormattedMessage defaultMessage="Not Deposited" />
              </StatusBadge>
              <StatusTitle>
                <FormattedMessage defaultMessage="Ready to deposit" />
              </StatusTitle>
            </StatusRow>
            <StatusDetail>
              <FormattedMessage defaultMessage="No deposit found for this validator. Click 'Confirm deposit' to proceed." />
            </StatusDetail>
          </StatusContainer>
        );

      case ValidatorDepositStatus.DEPOSITED_WAITING:
        return (
          <StatusContainer variant="warning">
            <StatusRow>
              <StatusBadge variant="warning">
                <FormattedMessage defaultMessage="Deposited" />
              </StatusBadge>
              <StatusTitle>
                <FormattedMessage defaultMessage="Waiting for beacon chain" />
              </StatusTitle>
            </StatusRow>
            <StatusDetail>
              <FormattedMessage defaultMessage="Deposit confirmed on execution layer. The beacon chain typically processes deposits within 12-18 hours." />
            </StatusDetail>
            {execDepositIndex !== null && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Deposit index: {index}"
                  values={{
                    index: <DepositIndex>{execDepositIndex}</DepositIndex>,
                  }}
                />
              </StatusDetail>
            )}
            {execTxHash && (
              <ExternalLink
                to={`${ETHERSCAN_URL}/${execTxHash}`}
                shouldOpenNewTab
                primary
                inline
              >
                <FormattedMessage defaultMessage="View transaction" />
              </ExternalLink>
            )}
          </StatusContainer>
        );

      case ValidatorDepositStatus.PENDING_INIT:
        return (
          <StatusContainer variant="warning">
            <StatusRow>
              <StatusBadge variant="warning">
                <FormattedMessage defaultMessage="Pending" />
              </StatusBadge>
              <StatusTitle>
                <FormattedMessage defaultMessage="Initializing" />
              </StatusTitle>
            </StatusRow>
            {beaconIndex !== null && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Validator index: {index}"
                  values={{
                    index: <ValidatorIndex>{beaconIndex}</ValidatorIndex>,
                  }}
                />
              </StatusDetail>
            )}
            <StatusDetail>
              <FormattedMessage defaultMessage="Your validator has been accepted by the beacon chain and is pending activation." />
            </StatusDetail>
            {pubkey && (
              <ExternalLink
                to={`${BEACONCHAIN_URL}/validator/0x${pubkey}`}
                shouldOpenNewTab
                primary
                inline
              >
                <FormattedMessage defaultMessage="View on Beacon Explorer" />
              </ExternalLink>
            )}
          </StatusContainer>
        );

      case ValidatorDepositStatus.PENDING_QUEUED:
        return (
          <StatusContainer variant="warning">
            <StatusRow>
              <StatusBadge variant="warning">
                <FormattedMessage defaultMessage="Queued" />
              </StatusBadge>
              <StatusTitle>
                <FormattedMessage defaultMessage="In activation queue" />
              </StatusTitle>
            </StatusRow>
            {beaconIndex !== null && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Validator index: {index}"
                  values={{
                    index: <ValidatorIndex>{beaconIndex}</ValidatorIndex>,
                  }}
                />
              </StatusDetail>
            )}
            <StatusDetail>
              <FormattedMessage defaultMessage="Your validator is in the activation queue and will become active soon." />
            </StatusDetail>
            {pubkey && (
              <ExternalLink
                to={`${BEACONCHAIN_URL}/validator/0x${pubkey}`}
                shouldOpenNewTab
                primary
                inline
              >
                <FormattedMessage defaultMessage="View on Beacon Explorer" />
              </ExternalLink>
            )}
          </StatusContainer>
        );

      case ValidatorDepositStatus.ACTIVE:
        return (
          <StatusContainer variant="success">
            <StatusRow>
              <StatusBadge variant="success">
                <FormattedMessage defaultMessage="Active" />
              </StatusBadge>
              <StatusTitle>
                <FormattedMessage defaultMessage="Earning rewards" />
              </StatusTitle>
            </StatusRow>
            {beaconIndex !== null && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Validator index: {index}"
                  values={{
                    index: <ValidatorIndex>{beaconIndex}</ValidatorIndex>,
                  }}
                />
              </StatusDetail>
            )}
            {beaconBalance && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Effective balance: {balance} gwei"
                  values={{ balance: beaconBalance }}
                />
              </StatusDetail>
            )}
            {pubkey && (
              <ExternalLink
                to={`${BEACONCHAIN_URL}/validator/0x${pubkey}`}
                shouldOpenNewTab
                primary
                inline
              >
                <FormattedMessage defaultMessage="View on Beacon Explorer" />
              </ExternalLink>
            )}
          </StatusContainer>
        );

      case ValidatorDepositStatus.EXITING:
        return (
          <StatusContainer variant="info">
            <StatusRow>
              <StatusBadge variant="info">
                <FormattedMessage defaultMessage="Exiting" />
              </StatusBadge>
              <StatusTitle>
                <FormattedMessage defaultMessage="Validator is exiting" />
              </StatusTitle>
            </StatusRow>
            {beaconIndex !== null && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Validator index: {index}"
                  values={{
                    index: <ValidatorIndex>{beaconIndex}</ValidatorIndex>,
                  }}
                />
              </StatusDetail>
            )}
            {pubkey && (
              <ExternalLink
                to={`${BEACONCHAIN_URL}/validator/0x${pubkey}`}
                shouldOpenNewTab
                primary
                inline
              >
                <FormattedMessage defaultMessage="View on Beacon Explorer" />
              </ExternalLink>
            )}
          </StatusContainer>
        );

      case ValidatorDepositStatus.EXITED:
      case ValidatorDepositStatus.WITHDRAWN:
        return (
          <StatusContainer variant="info">
            <StatusRow>
              <StatusBadge variant="info">
                {status === ValidatorDepositStatus.WITHDRAWN ? (
                  <FormattedMessage defaultMessage="Withdrawn" />
                ) : (
                  <FormattedMessage defaultMessage="Exited" />
                )}
              </StatusBadge>
              <StatusTitle>
                <FormattedMessage defaultMessage="Validator has exited" />
              </StatusTitle>
            </StatusRow>
            {beaconIndex !== null && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Validator index: {index}"
                  values={{
                    index: <ValidatorIndex>{beaconIndex}</ValidatorIndex>,
                  }}
                />
              </StatusDetail>
            )}
            {pubkey && (
              <ExternalLink
                to={`${BEACONCHAIN_URL}/validator/0x${pubkey}`}
                shouldOpenNewTab
                primary
                inline
              >
                <FormattedMessage defaultMessage="View on Beacon Explorer" />
              </ExternalLink>
            )}
          </StatusContainer>
        );

      case ValidatorDepositStatus.SLASHED:
        return (
          <StatusContainer variant="error">
            <StatusRow>
              <StatusBadge variant="error">
                <FormattedMessage defaultMessage="Slashed" />
              </StatusBadge>
              <StatusTitle>
                <FormattedMessage defaultMessage="Validator was slashed" />
              </StatusTitle>
            </StatusRow>
            {beaconIndex !== null && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Validator index: {index}"
                  values={{
                    index: <ValidatorIndex>{beaconIndex}</ValidatorIndex>,
                  }}
                />
              </StatusDetail>
            )}
            {pubkey && (
              <ExternalLink
                to={`${BEACONCHAIN_URL}/validator/0x${pubkey}`}
                shouldOpenNewTab
                primary
                inline
              >
                <FormattedMessage defaultMessage="View on Beacon Explorer" />
              </ExternalLink>
            )}
          </StatusContainer>
        );

      case ValidatorDepositStatus.ERROR:
      default:
        return (
          <StatusContainer variant="error">
            <StatusRow>
              <StatusBadge variant="error">
                <FormattedMessage defaultMessage="Error" />
              </StatusBadge>
              <StatusTitle>
                <FormattedMessage defaultMessage="Unable to check status" />
              </StatusTitle>
            </StatusRow>
            <StatusDetail>
              <FormattedMessage defaultMessage="There was an error checking the validator status. Please try again." />
            </StatusDetail>
          </StatusContainer>
        );
    }
  };

  if (compact) {
    return (
      <StatusBadge variant={getStatusVariant()}>
        {status === ValidatorDepositStatus.NOT_DEPOSITED && (
          <FormattedMessage defaultMessage="Not Deposited" />
        )}
        {status === ValidatorDepositStatus.DEPOSITED_WAITING && (
          <FormattedMessage defaultMessage="Waiting" />
        )}
        {status === ValidatorDepositStatus.PENDING_INIT && (
          <FormattedMessage defaultMessage="Pending" />
        )}
        {status === ValidatorDepositStatus.PENDING_QUEUED && (
          <FormattedMessage defaultMessage="Queued" />
        )}
        {status === ValidatorDepositStatus.ACTIVE && (
          <FormattedMessage defaultMessage="Active" />
        )}
        {status === ValidatorDepositStatus.EXITING && (
          <FormattedMessage defaultMessage="Exiting" />
        )}
        {status === ValidatorDepositStatus.EXITED && (
          <FormattedMessage defaultMessage="Exited" />
        )}
        {status === ValidatorDepositStatus.SLASHED && (
          <FormattedMessage defaultMessage="Slashed" />
        )}
        {status === ValidatorDepositStatus.WITHDRAWN && (
          <FormattedMessage defaultMessage="Withdrawn" />
        )}
        {status === ValidatorDepositStatus.ERROR && (
          <FormattedMessage defaultMessage="Error" />
        )}
      </StatusBadge>
    );
  }

  return renderStatusContent();
};

export default ValidatorStatusDisplay;
